import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { signedFetch } from '../../services/api';

interface ManagedProject {
  projectId: string;
  projectName: string;
  message: string;
  agentAccessFolderLink: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

function hasMessageTemplate(project: ManagedProject): boolean {
  return project.message.trim().length > 0;
}

function MessageTemplateMark({ ready }: { ready: boolean }) {
  const Icon = ready ? CheckCircleIcon : CancelIcon;
  return (
    <Icon
      sx={{ fontSize: 18, color: ready ? '#16a34a' : '#dc2626', flexShrink: 0 }}
      aria-hidden
    />
  );
}

function MessageTemplateLegend() {
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.75 }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <MessageTemplateMark ready />
        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
          Has a message template
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <MessageTemplateMark ready={false} />
        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
          No message template
        </Typography>
      </Stack>
    </Stack>
  );
}

const MESSAGE_PLACEHOLDERS = ['client_name', 'agent_name', 'edm_link'];

function insertPlaceholder(current: string, name: string): string {
  const spacer = current.endsWith(' ') || current.endsWith('\n') || !current ? '' : ' ';
  return `${current}${spacer}{{${name}}}`;
}

function Section({
  title,
  help,
  children,
}: {
  title: string;
  help: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid rgba(var(--color-text-rgb), 0.12)',
      }}
    >
      <Typography sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 0.5, mb: 1.5 }}>
        {help}
      </Typography>
      {children}
    </Box>
  );
}

function PlaceholderChips({
  disabled,
  onInsert,
}: {
  disabled: boolean;
  onInsert: (name: string) => void;
}) {
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
      {MESSAGE_PLACEHOLDERS.map((name) => (
        <Chip
          key={name}
          size="small"
          label={`{{${name}}}`}
          disabled={disabled}
          onClick={() => onInsert(name)}
          sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
        />
      ))}
    </Stack>
  );
}

/**
 * Optional share settings. Admins and ICs edit a project's Custom message and
 * Agent Access Folder. Project Dept and admins edit the untagged Custom message.
 */
export default function ManageProjectMessagesDialog({ open, onClose }: Props) {
  const [mode, setMode] = useState<'generic' | 'projects'>('projects');
  const [canEditGeneric, setCanEditGeneric] = useState(false);
  const [projects, setProjects] = useState<ManagedProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ManagedProject | null>(null);
  const [message, setMessage] = useState('');
  const [folderLink, setFolderLink] = useState('');
  const [genericMessage, setGenericMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSelected(null);
    setMessage('');
    setFolderLink('');
    setGenericMessage('');
    setSaveError(null);
    setSaved(false);
    void (async () => {
      try {
        const res = await signedFetch('/api/project-share-messages');
        const payload = await res.json().catch(() => ({})) as {
          error?: string;
          mode?: 'generic' | 'projects';
          genericMessage?: string | null;
          projects?: ManagedProject[];
        };
        if (!res.ok) throw new Error(payload.error || 'Failed to load projects');
        if (cancelled) return;
        setMode(payload.mode === 'generic' ? 'generic' : 'projects');
        setCanEditGeneric(typeof payload.genericMessage === 'string');
        setGenericMessage(typeof payload.genericMessage === 'string' ? payload.genericMessage : '');
        setProjects(Array.isArray(payload.projects) ? payload.projects : []);
      } catch (err) {
        if (cancelled) return;
        setProjects([]);
        setLoadError(err instanceof Error ? err.message : 'Failed to load projects');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const chooseProject = (project: ManagedProject | null) => {
    setSelected(project);
    setMessage(project?.message || '');
    setFolderLink(project?.agentAccessFolderLink || '');
    setSaveError(null);
    setSaved(false);
  };

  const handleSave = async () => {
    if (mode === 'projects' && !selected && !canEditGeneric) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      if (mode === 'projects' && selected) {
        const res = await signedFetch(`/api/project-share-messages/${encodeURIComponent(selected.projectId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message, agentAccessFolderLink: folderLink }),
        });
        const payload = await res.json().catch(() => ({})) as {
          error?: string;
          message?: string;
          agentAccessFolderLink?: string;
        };
        if (!res.ok) throw new Error(payload.error || 'Failed to save');
        const nextMessage = payload.message || '';
        const nextLink = payload.agentAccessFolderLink || '';
        setMessage(nextMessage);
        setFolderLink(nextLink);
        setProjects((prev) => prev.map((p) => (
          p.projectId === selected.projectId
            ? { ...p, message: nextMessage, agentAccessFolderLink: nextLink }
            : p
        )));
        setSelected((prev) => prev ? { ...prev, message: nextMessage, agentAccessFolderLink: nextLink } : prev);
      }
      if (canEditGeneric) {
        const res = await signedFetch('/api/project-share-messages/generic', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: genericMessage }),
        });
        const payload = await res.json().catch(() => ({})) as { error?: string; message?: string };
        if (!res.ok) throw new Error(payload.error || 'Failed to save');
        setGenericMessage(payload.message || '');
      }
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const canSave = mode === 'generic' || !!selected || canEditGeneric;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 700, color: 'var(--color-text-strong)' }}>
        Sharables Setup
        <IconButton aria-label="Close" onClick={onClose} disabled={saving} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.1)' }}>
        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2}>
            {mode === 'projects' && (
              <Box>
                <Autocomplete
                  options={projects}
                  value={selected}
                  onChange={(_e, next) => chooseProject(next)}
                  getOptionLabel={(opt) => opt.projectName}
                  isOptionEqualToValue={(a, b) => a.projectId === b.projectId}
                  noOptionsText="No projects to manage"
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
                        <MessageTemplateMark ready={hasMessageTemplate(option)} />
                        <Typography noWrap sx={{ minWidth: 0 }}>{option.projectName}</Typography>
                      </Stack>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Project"
                      placeholder="Search projects"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: selected ? (
                          <>
                            <MessageTemplateMark ready={hasMessageTemplate(selected)} />
                            {params.InputProps.startAdornment}
                          </>
                        ) : params.InputProps.startAdornment,
                      }}
                    />
                  )}
                />
                <MessageTemplateLegend />
              </Box>
            )}
            {canEditGeneric && (
              <Section
                title="Untagged files"
                help="The Custom tab text when a file is not tagged to a project. Leave blank to hide that tab."
              >
                <TextField
                  label="Custom message"
                  value={genericMessage}
                  onChange={(e) => { setGenericMessage(e.target.value); setSaved(false); }}
                  fullWidth
                  multiline
                  minRows={6}
                  maxRows={14}
                />
                <PlaceholderChips
                  disabled={false}
                  onInsert={(name) => {
                    setSaved(false);
                    setGenericMessage((current) => insertPlaceholder(current, name));
                  }}
                />
              </Section>
            )}
            {mode === 'projects' && (
              <Section
                title="Custom WhatsApp message"
                help="The message template shown when agents share an EDM from project essentials."
              >
                <TextField
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); setSaved(false); }}
                  disabled={!selected}
                  fullWidth
                  multiline
                  minRows={6}
                  maxRows={14}
                  placeholder={selected ? '' : 'Select a project'}
                />
                <PlaceholderChips
                  disabled={!selected}
                  onInsert={(name) => {
                    setSaved(false);
                    setMessage((current) => insertPlaceholder(current, name));
                  }}
                />
              </Section>
            )}
            {mode === 'projects' && (
              <Section
                title="Agent Access Folder"
                help="The link agents already open from Project Essentials. Leave blank if there is no folder."
              >
                <TextField
                  value={folderLink}
                  onChange={(e) => { setFolderLink(e.target.value); setSaved(false); }}
                  disabled={!selected}
                  fullWidth
                  placeholder="https://"
                />
              </Section>
            )}
            {saveError && <Alert severity="error">{saveError}</Alert>}
            {saved && <Alert severity="success">Saved.</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Button variant="contained" onClick={() => { void handleSave(); }} disabled={!canSave || saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
